/**
 * Actions from meetings that are not tied to a creative (Aziz, 2026-09-28): "book the
 * dental chair", "walk the team through the new system". They sit in My tasks until the
 * person marks them done; the organiser is told.
 */
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Check } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { fetchMyActions, markActionDone } from '@/lib/calls';

function day(d?: string | null) {
  return d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
}

export default function MeetingActions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['meeting-actions'], queryFn: fetchMyActions });
  const done = useMutation({
    mutationFn: markActionDone,
    onSuccess: () => { toast.success('Marked done. The organiser is told.'); qc.invalidateQueries({ queryKey: ['meeting-actions'] }); },
    onError: () => toast.error('That did not go through. Try again.'),
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const open = rows.filter((r) => r.status === 'sent');

  return (
    <Card className="mb-5" data-testid="meeting-actions">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted mb-2">
        <CalendarClock size={12} /> From meetings {open.length > 0 && <span className="text-muted-strong">· {open.length} open</span>}
      </div>
      <ul className="divide-y divide-slate-800">
        {rows.map((r) => {
          const late = r.status === 'sent' && !!r.due_date && r.due_date < today;
          return (
            <li key={r.id} className="py-2.5 flex flex-wrap items-start gap-3" data-testid="meeting-action">
              <div className="flex-1 min-w-[12rem]">
                <div className={cn('text-sm', r.status === 'done' ? 'text-muted line-through' : 'text-muted-strong')}>{r.title}</div>
                <div className="text-xs text-muted mt-0.5">
                  <Link to={`/meetings/${r.meeting_id}`} className="hover:text-accent-400">{r.meeting_title}</Link>
                  {r.organizer_name && <> · from {r.organizer_name}</>}
                  {r.due_date && <span className={cn(late && 'text-red-300')}> · due {day(r.due_date)}</span>}
                  {r.when_said && <> · said: “{r.when_said}”</>}
                </div>
                {r.notes && <div className="text-xs text-muted mt-1 whitespace-pre-wrap">{r.notes}</div>}
              </div>
              {r.status === 'sent' ? (
                <Button size="sm" variant="secondary" icon={<Check size={13} />} loading={done.isPending && done.variables === r.id}
                  onClick={() => done.mutate(r.id)} data-testid="action-done">Done</Button>
              ) : <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Check size={12} /> Done</span>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
